import React, { useEffect, useRef } from 'react';

/** KSA Associates Head Office, always shown with plant registration locations. */
export const HEAD_OFFICE = {
  id: 'head-office',
  name: 'Head Office',
  area: 'No. 1, Ekambaram Main Road, Pammal, Chennai 600075',
  lat: 12.9749,
  lng: 80.1328,
};

/** KSA plant: SIPCOT Main Road, Mambakkam, Sriperumbudur 602106. */
export const PLANT_LOCATION = {
  id: 'plant-location',
  name: 'Plant Location',
  area: 'No. 287/6 & 7, Sipcot Main Road, Mambakkam, Madura Selaiyanur Village, Sriperumbudur Taluk, Kanchipuram, Pincode 602106',
  lat: 12.9244,
  lng: 79.9168,
};

export const PLANT_LOCATIONS = [HEAD_OFFICE, PLANT_LOCATION];

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const DEFAULT_CENTER = [12.9749, 80.1328];
const DEFAULT_ZOOM = 13;

const PIN_SVG = `
  <svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true" focusable="false">
    <path
      d="M12 1.5C7.3 1.5 3.5 5.3 3.5 10c0 6.4 7.2 14.2 8.1 15.1a0.7 0.7 0 0 0 1 0C13.4 24.2 20.5 16.4 20.5 10 20.5 5.3 16.7 1.5 12 1.5z"
      fill="currentColor"
      stroke="#061228"
      stroke-width="1.2"
    />
    <circle cx="12" cy="10" r="3.4" fill="#ffffff" />
  </svg>
`;

function ensureLeafletAssets() {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L));
      existing.addEventListener('error', reject);
      if (window.L) resolve(window.L);
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function createPinIcon(L, active = false) {
  return L.divIcon({
    className: `login-plant-pin${active ? ' is-active' : ''}`,
    html: PIN_SVG,
    iconSize: active ? [30, 40] : [26, 34],
    iconAnchor: active ? [15, 40] : [13, 34],
    tooltipAnchor: [0, -36],
  });
}

function LoginPlantMap({
  locations = PLANT_LOCATIONS,
  activeName = '',
  onSelect,
}) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let cancelled = false;

    ensureLeafletAssets()
      .then((L) => {
        if (cancelled || !mapNodeRef.current || mapRef.current) return;

        const mapped = locations.filter((plant) => Number.isFinite(plant.lat) && Number.isFinite(plant.lng));
        const map = L.map(mapNodeRef.current, {
          center: mapped[0] ? [mapped[0].lat, mapped[0].lng] : DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          zoomControl: false,
          attributionControl: true,
          scrollWheelZoom: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 18,
          attribution: '&copy; OpenStreetMap',
        }).addTo(map);

        L.control.zoom({ position: 'bottomright' }).addTo(map);

        const markers = {};
        mapped.forEach((plant) => {
          const plantKey = String(plant.id || plant.name);
          const marker = L.marker([plant.lat, plant.lng], {
            icon: createPinIcon(L, false),
            riseOnHover: true,
          })
            .addTo(map)
            .bindTooltip(plant.name, {
              direction: 'top',
              offset: [0, -8],
              opacity: 0.95,
            });

          marker.on('click', () => {
            onSelectRef.current?.(plantKey);
          });

          markers[plantKey] = marker;
        });

        markersRef.current = markers;
        mapRef.current = map;

        const points = mapped.map((plant) => [plant.lat, plant.lng]);
        if (points.length === 1) {
          map.setView(points[0], DEFAULT_ZOOM);
        } else if (points.length > 1) {
          map.fitBounds(points, { padding: [28, 28], maxZoom: 12 });
        }

        window.setTimeout(() => {
          map.invalidateSize();
        }, 80);
      })
      .catch(() => {
        // Keep empty container if CDN load fails.
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markersRef.current = {};
      }
    };
  }, [locations]);

  useEffect(() => {
    const L = window.L;
    const map = mapRef.current;
    const markers = markersRef.current;
    if (!L || !map || !markers) return;

    Object.entries(markers).forEach(([name, marker]) => {
      const active = name === activeName;
      marker.setIcon(createPinIcon(L, active));
      if (active) {
        marker.setZIndexOffset(600);
        marker.openTooltip();
        map.panTo(marker.getLatLng(), { animate: true });
      } else {
        marker.setZIndexOffset(0);
        marker.closeTooltip();
      }
    });
  }, [activeName]);

  return (
    <div className="login-plant-map" aria-label="KSA Associates plant location map">
      <div ref={mapNodeRef} className="login-plant-map__canvas" />
      <div className="login-plant-map__badge">
        {locations.find((location) => String(location.id || location.name) === String(activeName))?.name || 'Locations'}
      </div>
    </div>
  );
}

export default LoginPlantMap;
